import { Helmet } from "react-helmet";
import { FC, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import AffectedReservationGridDetailedCard from "./MadeReservationGridDetailedCard ";
import CommonLayout from "../../AccountPage/CommonLayout";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaClock, FaTimes, FaFilter, FaCheck } from "react-icons/fa";

export interface AccountPageProps {
  className?: string;
}

const ClientMadeReservationPage: FC<AccountPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const [selectedFilter, setSelectedFilter] = useState<string>("all");

  // Determine title and content based on route
  const isEventsPage = location.pathname === "/client/evenements";
  const title = isEventsPage ? "Gérez vos événements" : "Gérez vos réservations";
  const description = isEventsPage
    ? "Consultez et gérez tous vos événements. Suivez le statut de vos réservations et organisez vos occasions spéciales."
    : "Consultez et gérez toutes vos réservations d'espaces. Suivez le statut de vos demandes et organisez vos événements.";

  const filters = [
    { key: "all", label: "Toutes", icon: FaFilter, status: null },
    { key: "confirmed", label: "Confirmées", icon: FaCheckCircle, status: "En cours" },
    { key: "pending", label: "En attente", icon: FaClock, status: "En attente" },
    { key: "completed", label: "Terminées", icon: FaCheck, status: "Terminée" },
    { key: "cancelled", label: "Annulées", icon: FaTimes, status: "Annulée" },
  ];

  return (
    <CommonLayout>
      <div className={`nc-ClientMadeReservationPage ${className}`} data-nc-id="ClientMadeReservationPage">
        <Helmet>
          <title>Mes Réservations - EventToBee | Gérez vos réservations d'espaces</title>
          <meta
            name="description"
            content="Consultez et gérez toutes vos réservations d'espaces événementiels. Suivez le statut de vos demandes et organisez vos événements."
          />
        </Helmet>

        <div className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
          {/* Background Effects */}
          <BgGlassmorphism />
          <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

          {/* Hero Section */}
          <motion.div
            className="relative pt-8 lg:pt-12"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="container px-4 mx-auto max-w-7xl">
              <div className="text-center">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
                  {title}
                </h1>
                <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
                  {description}
                </p>

                {/* Statistics */}
                <div className="flex flex-wrap justify-center gap-6 mt-8">
                  <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                    <FaMapMarkerAlt className="w-5 h-5 text-pink-500 mr-2" />
                    <span className="text-neutral-700 dark:text-neutral-300 font-medium">Espaces confirmés</span>
                  </div>
                  <div className="flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
                    <FaCalendarAlt className="w-5 h-5 text-rose-500 mr-2" />
                    <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                      Événements planifiés
                    </span>
                  </div>
                </div>

                {/* Filters */}
                <div className="mt-12">
                  <div className="flex flex-wrap justify-center gap-4">
                    {filters.map((filter) => {
                      const IconComponent = filter.icon;
                      return (
                        <motion.button
                          key={filter.key}
                          onClick={() => setSelectedFilter(filter.key)}
                          className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                            selectedFilter === filter.key
                              ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg"
                              : "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-pink-50 dark:hover:bg-neutral-700 shadow-md"
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <IconComponent className="w-4 h-4 mr-2" />
                          {filter.label}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Reservations Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <AffectedReservationGridDetailedCard
              className="pb-24 lg:pb-28"
              selectedFilter={selectedFilter}
              filterStatus={filters.find((f) => f.key === selectedFilter)?.status || null}
            />
          </motion.div>
        </div>
      </div>
    </CommonLayout>
  );
};

export default ClientMadeReservationPage;
